import { isDefined } from "@clipboard-health/util-ts";
import { FullScreenDialog } from "@src/appV2/lib";
import { convertToGeoLocation, convertToGoogleMapsLocation } from "@src/appV2/Location";
import { StickyScrollPageHeader } from "@src/appV2/redesign/components/StickyScrollPageHeader";
import { SHIFT_DISCOVERY_WORKPLACE_OPEN_SHIFTS_FULL_PATH } from "@src/appV2/redesign/ShiftDiscovery/paths";
import type { UseModalState } from "@src/appV2/redesign/system/ui-components";
import { WorkplaceModalContent } from "@src/appV2/redesign/Workplace/Modal/Content";
import { useWorkplaceOpenShiftsData } from "@src/appV2/redesign/Workplace/OpenShifts/useWorkplaceOpenShiftsData";
import { useWorkplaceStaffJobsData } from "@src/appV2/redesign/Workplace/OpenShifts/useWorkplaceStaffJobsData";
import { WorkplaceProfileModalContentSkeleton } from "@src/appV2/redesign/Workplace/Profile/ModalContentSkeleton";
import { WorkplaceProfileModalFooter } from "@src/appV2/redesign/Workplace/Profile/ModalFooter";
import { type WorkplaceProfile } from "@src/appV2/redesign/Workplace/types";
import { isHomeHealthOrHospiceWorkplace } from "@src/appV2/redesign/Workplace/utils";
import { useRef } from "react";
import { generatePath } from "react-router-dom";

import { useScrollToTopOnStatusTap } from "../../useScrollToTopOnStatusTap";
import { WorkplaceProfileOverview } from "./WorkplaceProfileOverview";

interface WorkplaceProfileModalProps {
  modalState: UseModalState;
  workplaceId: string;
  workplace?: WorkplaceProfile;
  isLoading?: boolean;
  sourcePlacementId?: string;
}

export function WorkplaceProfileModal(props: WorkplaceProfileModalProps) {
  const { modalState, workplaceId, workplace, isLoading, sourcePlacementId } = props;

  const {
    data: { unfilteredWorkplaceShifts },
    isLoading: openShiftsIsLoading,
  } = useWorkplaceOpenShiftsData(workplace);

  const {
    isJobsEnabled,
    isLoading: staffJobsIsLoading,
    placementsCount,
    placementCandidateId,
  } = useWorkplaceStaffJobsData(workplaceId);

  const hasOpenShifts = unfilteredWorkplaceShifts.length > 0;
  const hasStaffJobs = placementsCount > 0;
  const isFooterLoading = openShiftsIsLoading || staffJobsIsLoading;

  const scrollRef = useRef<HTMLDivElement>(null);

  useScrollToTopOnStatusTap(scrollRef);

  // Hide the primary open shifts CTA only when we know the workplace is HH/Hospice with no shifts.
  // While anything is still loading, keep the CTA slot so the unified skeleton sits in its place.
  const shouldShowOpenShiftsButton =
    isFooterLoading || !(isHomeHealthOrHospiceWorkplace(workplace?.type) && !hasOpenShifts);

  // Only shrink the content's bottom padding when the floating footer is fully empty; if
  // either the primary CTA or the staff-jobs button is showing, keep the default 200px pad
  // so the last bit of scrollable content isn't obscured by the absolutely-positioned footer.
  const hasAnyFooterButton = shouldShowOpenShiftsButton || (isJobsEnabled && hasStaffJobs);

  return (
    <FullScreenDialog skipTopSafeAreaPadding modalState={modalState} variant="primary">
      <StickyScrollPageHeader
        title={workplace?.name ?? ""}
        isLoading={isLoading}
        scrollContainerRef={scrollRef}
      />

      <WorkplaceModalContent
        ref={scrollRef}
        sx={hasAnyFooterButton ? undefined : { paddingBottom: 7 }}
      >
        {isLoading && !isDefined(workplace) && <WorkplaceProfileModalContentSkeleton />}
        {isDefined(workplace) ? (
          <WorkplaceProfileOverview
            name={workplace.name}
            type={String(workplace.type)}
            workplaceId={String(workplaceId)}
            phoneNumber={workplace.phone}
            location={convertToGoogleMapsLocation(
              convertToGeoLocation(workplace.geoLocation.coordinates)
            )}
            formattedAddress={workplace.fullAddress?.formatted}
            sourcePlacementId={sourcePlacementId}
          />
        ) : undefined}
      </WorkplaceModalContent>

      <WorkplaceProfileModalFooter
        workplaceId={workplaceId}
        isLoading={isFooterLoading}
        source="hiring-workplace-profile-modal"
        locationState={{ origin: "WORKPLACE_PROFILE_MODAL" as const }}
        openShifts={
          shouldShowOpenShiftsButton
            ? {
                count: unfilteredWorkplaceShifts.length,
                href: generatePath(SHIFT_DISCOVERY_WORKPLACE_OPEN_SHIFTS_FULL_PATH, {
                  workplaceId,
                }),
                analyticsTrigger: "Placements Workplace Profile Modal",
              }
            : undefined
        }
        staffJobs={
          isJobsEnabled && hasStaffJobs
            ? {
                placementsCount,
                placementCandidateId,
              }
            : undefined
        }
      />
    </FullScreenDialog>
  );
}
